import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Truck,
  Phone,
  Calendar,
  Package,
  MapPin,
  CheckCircle2,
  Clock,
  User,
  AlertTriangle,
  Upload,
  FileText,
  RotateCcw,
  Building2,
  Layers,
} from "lucide-react";
import { api, uploadFile, downloadDocument } from "../../lib/api";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import MoveBackStageModal from "./MoveBackStageModal";
import OrderHistoryTrail from "./OrderHistoryTrail";


function formatDateTime(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);

    const day = String(d.getDate()).padStart(2, "0");
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const month = months[d.getMonth()];
    const year = d.getFullYear();

    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return `${day} ${month} ${year}`;
    }

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = String(hours).padStart(2, "0");

    return `${day} ${month} ${year}, ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return String(val);
  }
}

export default function DeliveryDetail({ orderId, onBack, onChange, user }) {
  const [order, setOrder] = useState(null);
  const [client, setClient] = useState(null);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");

  // Delivery agent reassignment form
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [customAgentName, setCustomAgentName] = useState("");
  const [customAgentPhone, setCustomAgentPhone] = useState("");
  const [isChangingAgent, setIsChangingAgent] = useState(false);

  // Out for delivery input
  const [trackingNumber, setTrackingNumber] = useState("");

  // Delivery confirmation form
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [receivedBy, setReceivedBy] = useState("");
  const [deliveryRemarks, setDeliveryRemarks] = useState("");
  const [movingBack, setMovingBack] = useState(false);


  const canAct = isAdmin(user) || canAccessDepartment(user, ["Delivery", "Production", "Sales"]);
  const canAssignDelivery = isAdmin(user) || canAccessDepartment(user, ["Production"]);

  // 1. Load order and client details
  const loadOrder = useCallback(async (targetId = orderId) => {
    try {
      setLoading(true);
      const isDirectBatch = typeof targetId === "string" && targetId.toUpperCase().startsWith("BATCH-");

      let orderData = null;
      if (isDirectBatch) {
        try {
          const batchRes = await api("get", `/dispatch/batch/${targetId}`);
          if (batchRes.data?.orders?.length > 0) {
            orderData = {
              ...batchRes.data.orders[0],
              batch_orders: batchRes.data.orders,
              is_batch: true,
            };
          }
        } catch {
          // fallback
        }
      }

      if (!orderData) {
        const res = await api("get", `/orders/${targetId}`);
        orderData = res.data;
      }

      // If batch_orders not attached yet but order has upload_batch_id, attempt to load batch siblings
      if (orderData?.upload_batch_id && (!orderData.batch_orders || orderData.batch_orders.length <= 1)) {
        try {
          const bRes = await api("get", `/dispatch/batch/${orderData.upload_batch_id}`);
          if (bRes.data?.orders?.length > 1) {
            orderData.batch_orders = bRes.data.orders;
            orderData.is_batch = true;
          }
        } catch {
          // ignore batch lookup failure
        }
      }

      setOrder(orderData);
      setTrackingNumber(orderData.tracking_number || "");
      if (orderData.client_contact_person) {
        setReceivedBy(orderData.client_contact_person);
      }

      if (orderData.client_id) {
        try {
          const clientRes = await api("get", `/clients/${orderData.client_id}`);
          setClient(clientRes.data);
        } catch {
          // fallback to order client data
        }
      }
    } catch (err) {
      console.error("Failed to load delivery order:", err);
      toast.error("Could not load delivery details");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const handleSelectBatchOrder = (bo) => {
    setOrder((prev) => ({
      ...bo,
      batch_orders: prev?.batch_orders || [],
      is_batch: true,
      client_details: prev?.client_details || bo.client_details,
      history: bo.history || prev?.history || [],
    }));
    setTrackingNumber(bo.tracking_number || "");
    if (bo.client_contact_person) {
      setReceivedBy(bo.client_contact_person);
    }
  };

  // 2. Load delivery staff
  const loadDeliveryAgents = useCallback(async () => {
    try {
      const res = await api("get", "/users?department=Delivery&active_only=true");
      const list = Array.isArray(res.data) ? res.data : [];
      const validAgents = list.filter(
        (u) =>
          !u.is_deleted &&
          u.is_active !== false &&
          ((u.department || "").toLowerCase() === "delivery" || (u.role || "").toLowerCase() === "delivery")
      );
      setDeliveryAgents(validAgents);
    } catch (err) {
      console.error("Failed to load delivery staff:", err);
      setDeliveryAgents([]);
    }
  }, []);

  useEffect(() => {
    loadOrder();
    loadDeliveryAgents();
  }, [loadOrder, loadDeliveryAgents]);

  // Sync selected delivery agent
  useEffect(() => {
    if (order && deliveryAgents.length > 0) {
      if (order.delivery_person_id) {
        setSelectedAgentId(order.delivery_person_id);
      } else if (order.assigned_delivery_person) {
        const matched = deliveryAgents.find(
          (a) => a.name?.toLowerCase() === order.assigned_delivery_person?.toLowerCase()
        );
        if (matched) setSelectedAgentId(matched.user_id);
      }
    }
  }, [order, deliveryAgents]);

  // Handle Assign or Change Delivery Person
  const handleAssignDeliveryPerson = async (e) => {
    if (e) e.preventDefault();
    let name = "";
    let phone = "";
    let id = "";

    if (selectedAgentId === "custom") {
      name = customAgentName.trim();
      phone = customAgentPhone.trim();
      if (!name) {
        toast.error("Please enter the delivery person's name");
        return;
      }
    } else {
      const agent = deliveryAgents.find((a) => a.user_id === selectedAgentId);
      if (!agent) {
        toast.error("Please select a delivery person");
        return;
      }
      name = agent.name;
      phone = agent.phone || "";
      id = agent.user_id;
    }

    setBusyAction("assign");
    try {
      await api("patch", `/orders/${orderId}/assign-delivery`, {
        assigned_delivery_person: name,
        delivery_person_phone: phone,
        delivery_person_id: id,
      });
      toast.success(`Assigned ${name} as delivery person`);
      setIsChangingAgent(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not assign delivery person";
      toast.error(typeof msg === "string" ? msg : "Assignment failed");
    } finally {
      setBusyAction("");
    }
  };

  // Handle Mark Out for Delivery
  const handleOutForDelivery = async () => {
    setBusyAction("out-for-delivery");
    try {
      await api("post", `/orders/${orderId}/out-for-delivery`, {
        transporter: order?.assigned_delivery_person || "In-House Delivery",
        tracking_number: trackingNumber.trim(),
      });
      toast.success("Order status updated to Out for Delivery!");
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not mark order out for delivery";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyAction("");
    }
  };

  const hasChallan = Boolean(
    order?.has_challan ||
    order?.challan_document_id ||
    (order?.challans && order.challans.length > 0) ||
    (order?.documents && order.documents.some((d) => d.category === "Challan" || d.category === "Delivery Challan"))
  );

  // Handle Upload Challan
  const handleUploadChallan = async (file) => {
    if (!file) return;
    setBusyAction("upload-challan");
    try {
      await uploadFile(orderId, file, "Challan");
      toast.success("Challan uploaded successfully");
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      console.error("Failed to upload challan:", err);
      const msg = err.response?.data?.detail || "Failed to upload challan";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setBusyAction("");
    }
  };

  // Handle Mark Delivery Complete
  const handleMarkDeliveryComplete = async (e) => {
    if (e) e.preventDefault();
    if (!hasChallan) {
      toast.error("Please upload a challan before marking this delivery as delivered.");
      return;
    }
    if (!receivedBy.trim()) {
      toast.error("Please enter the recipient's name");
      return;
    }

    setBusyAction("deliver");
    try {
      await api("post", `/orders/${orderId}/deliveries`, {
        received_by: receivedBy.trim(),
        remarks: deliveryRemarks.trim(),
        actual_date: new Date().toISOString().slice(0, 10),
        status: "Delivered",
      });
      toast.success("Delivery marked complete!");
      setShowCompleteForm(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to mark delivery complete";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyAction("");
    }
  };

  if (loading && !order) {
    return (
      <div className="content">
        <div className="loading-screen">Loading delivery details…</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="content">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Delivery
        </button>
        <div className="panel" style={{ padding: 40, textAlign: "center", marginTop: 20 }}>
          <h3>Delivery order not found</h3>
          <p className="muted">This order might have been moved or deleted.</p>
        </div>
      </div>
    );
  }

  // Unified 3-stage delivery status computation:
  // 1. "Delivered"
  // 2. "Out for Delivery"
  // 3. "Ready for Delivery"
  const isDelivered =
    order.delivery_status === "Delivered" ||
    order.delivery_status === "Completed" ||
    order.dispatch_status === "Delivered";

  const isOutForDelivery =
    !isDelivered &&
    (order.delivery_status === "Out for Delivery" ||
      order.delivery_status === "In Transit" ||
      order.dispatch_status === "Out for Delivery" ||
      order.dispatch_status === "Dispatched" ||
      order.current_stage === "Delivery");

  const currentStage = isDelivered
    ? "Delivered"
    : isOutForDelivery
    ? "Out for Delivery"
    : "Ready for Delivery";

  const statusBadge = isDelivered ? "green" : isOutForDelivery ? "blue" : "amber";

  // Address
  const shippingAddress =
    order.delivery_address ||
    client?.shipping_address ||
    client?.billing_address ||
    order.client_address ||
    "Standard client address on file";

  // Contact
  const contactPerson =
    order.client_contact_person ||
    client?.contact_person ||
    order.client_name ||
    "—";
  const contactPhone = canViewClientPhone(user) ? (order.client_phone || client?.phone || "") : "";
  const contactEmail = order.client_email || client?.email || "";

  const isDeliveryPersonAssigned = Boolean(order.assigned_delivery_person);

  return (
    <div className="content" style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* Back Button */}
      <button
        className="back-btn"
        onClick={onBack}
      >
        <ArrowLeft size={14} /> Back to Delivery
      </button>

      {/* Clean Header */}
      <div className="detail-head" style={{ marginBottom: 20 }}>
        <div>
          <div className="small-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            DELIVERY WORKSPACE · #{order.order_id}
          </div>
          <h1 style={{ marginTop: 4, marginBottom: 2 }} data-testid="delivery-product-title">{order.product}</h1>
          {/* Order Description — displayed immediately below product name, matching Dispatch Details header */}
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: order.description?.trim() ? "#0f172a" : "#94a3b8",
              fontStyle: order.description?.trim() ? "normal" : "italic",
              lineHeight: 1.5,
              margin: "4px 0 10px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
            data-testid="delivery-order-description-text"
          >
            {order.description?.trim() ? order.description.trim() : "No description provided"}
          </div>
          <p className="muted" style={{ margin: 0 }}>
            {order.client_name} · {(order.quantity || 0).toLocaleString()} units · Due{" "}
            {order.required_delivery_date || "—"}
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {order.current_stage !== "Order Received" && (
            <button
              type="button"
              className="outline-btn compact"
              data-testid="delivery-header-move-back-btn"
              style={{
                color: "#b45309",
                borderColor: "#fde68a",
                background: "#fffbeb",
                fontWeight: 600,
                fontSize: 12,
              }}
              onClick={() => setMovingBack(true)}
              title="Move order back to previous stage"
            >
              <RotateCcw size={12} /> Move Back
            </button>
          )}
          <span className={`badge ${statusBadge}`} style={{ fontSize: 12, padding: "5px 12px" }}>
            {currentStage}
          </span>
          {order.priority && (
            <span
              className={`badge ${
                order.priority === "Urgent" ? "red" : order.priority === "High" ? "amber" : "blue"
              }`}
              style={{ fontSize: 12, padding: "5px 12px" }}
            >
              {order.priority}
            </span>
          )}
          {order.delivery_sequence && order.assigned_delivery_person && (
            <span
              className="badge"
              style={{
                fontSize: 12,
                padding: "5px 12px",
                background: "#0f172a",
                color: "#ffffff",
                fontWeight: 700,
              }}
              title={`Delivery sequence #${order.delivery_sequence} for ${order.assigned_delivery_person}`}
              data-testid="delivery-detail-seq-badge"
            >
              Stop #{order.delivery_sequence} · {order.assigned_delivery_person}
            </span>
          )}
        </div>
      </div>


      {/* 3-Stage Delivery Workflow Progress Indicator */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          background: "#fff",
          border: "1px solid var(--line)",
          borderRadius: 8,
          marginBottom: 22,
          fontSize: 12.5,
          color: "#475569",
          overflowX: "auto",
        }}
      >
        <span style={{ fontWeight: 700, color: "#0f172a" }}>Delivery Stage:</span>
        <span
          style={{
            fontWeight: 600,
            color: currentStage === "Ready for Delivery" ? "#ca8a04" : "#16a34a",
          }}
        >
          1. Ready for Delivery
        </span>
        <span style={{ color: "#cbd5e1" }}>→</span>
        <span
          style={{
            fontWeight: 600,
            color:
              currentStage === "Out for Delivery"
                ? "#2563eb"
                : isDelivered
                ? "#16a34a"
                : "#94a3b8",
          }}
        >
          2. Out for Delivery
        </span>
        <span style={{ color: "#cbd5e1" }}>→</span>
        <span
          style={{
            fontWeight: 600,
            color: isDelivered ? "#16a34a" : "#94a3b8",
          }}
        >
          3. Delivered
        </span>
      </div>

      {/* 2-Column Clean CRM Layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 22, alignItems: "flex-start" }}>
        {/* LEFT COLUMN: Client & Delivery Information */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Card 1: Client & Delivery Destination */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>Client & Delivery Destination</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Recipient contact and delivery location details
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                <span className="muted">Client / Company:</span>
                <b style={{ color: "#0f172a" }}>{order.client_name}</b>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                <span className="muted">Contact Person:</span>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{contactPerson}</span>
              </div>

              {canViewClientPhone(user) && (
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5, alignItems: "center" }}>
                  <span className="muted">Contact Number:</span>
                  <div>
                    {contactPhone ? (
                      <a
                        href={`tel:${contactPhone}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          color: "#2563eb",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        <Phone size={13} /> {contactPhone}
                      </a>
                    ) : (
                      <span className="muted">Not specified</span>
                    )}
                  </div>
                </div>
              )}

              {contactEmail && (
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                  <span className="muted">Email:</span>
                  <span>{contactEmail}</span>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
                <span className="muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <MapPin size={13} /> Delivery Address:
                </span>
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid var(--line)",
                    color: "#1e293b",
                    lineHeight: 1.45,
                  }}
                >
                  {shippingAddress}
                </div>
              </div>
            </div>
          </section>

          {/* Card: Pickup Origin Location */}
          <section className="panel" data-testid="delivery-pickup-panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>Pickup Origin Location</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Origin warehouse or facility where shipment is picked up
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                <span className="muted">Origin Type:</span>
                <span style={{ fontWeight: 600, color: "#4f46e5" }}>
                  {order.pickup_type || (order.warehouse_name ? "Warehouse" : order.production_vendor_name ? "Production Facility" : "Central Hub")}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                <span className="muted">Location Name:</span>
                <b style={{ color: "#0f172a" }}>
                  {order.pickup_location_name || order.warehouse_name || order.production_vendor_name || "Central Logistics Hub"}
                </b>
              </div>

              {order.pickup_contact_person && (
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                  <span className="muted">Contact Person:</span>
                  <span style={{ fontWeight: 600, color: "#1e293b" }}>{order.pickup_contact_person}</span>
                </div>
              )}

              {canViewClientPhone(user) && order.pickup_phone && (
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5, alignItems: "center" }}>
                  <span className="muted">Pickup Phone:</span>
                  <div>
                    <a
                      href={`tel:${order.pickup_phone}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        color: "#2563eb",
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      <Phone size={13} /> {order.pickup_phone}
                    </a>
                  </div>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
                <span className="muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Building2 size={13} /> Pickup Address:
                </span>
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid var(--line)",
                    color: "#1e293b",
                    lineHeight: 1.45,
                  }}
                >
                  {order.pickup_address || "Central Logistics Hub, GT Karnal Road, Delhi"}
                </div>
              </div>
            </div>
          </section>

          {/* Card 2: Product & Order Details */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>Product & Quantity Details</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Package specifications and commitment deadline
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  background: "#f8fafc",
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                }}
              >
                <div>
                  <b style={{ fontSize: 14, color: "#0f172a" }}>{order.product}</b>
                  {order.product_code && (
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                      Code: {order.product_code}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
                    {(order.quantity || 0).toLocaleString()}
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Units
                  </div>
                </div>
              </div>

              {/* Order Description Field */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: 8,
                  fontSize: 13,
                  background: "#f8fafc",
                  padding: "10px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--line)",
                }}
                data-testid="delivery-order-description-field"
              >
                <span className="muted" style={{ fontWeight: 600 }}>Order Description:</span>
                <div
                  style={{
                    color: order.description?.trim() ? "#0f172a" : "#94a3b8",
                    fontStyle: order.description?.trim() ? "normal" : "italic",
                    fontWeight: order.description?.trim() ? 500 : 400,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                  data-testid="delivery-order-description-text"
                >
                  {order.description?.trim() ? order.description.trim() : "No description provided"}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Delivery Deadline:</span>
                <b>{order.required_delivery_date || "—"}</b>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Production Vendor:</span>
                <b style={{ color: "#4f46e5" }}>{order.production_vendor_name || order.assigned_producer || "—"}</b>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Procurement WH:</span>
                <b style={{ color: "#0f172a" }}>{order.warehouse_name || "—"}</b>
              </div>

              {order.challan_number && (
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, fontSize: 13 }}>
                  <span className="muted">Challan Number:</span>
                  <span className="badge green" style={{ fontSize: 11 }}>
                    {order.challan_number}
                  </span>
                </div>
              )}

              {order.tracking_number && (
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, fontSize: 13 }}>
                  <span className="muted">Tracking / Vehicle:</span>
                  <span style={{ color: "#1e293b", fontWeight: 600 }}>{order.tracking_number}</span>
                </div>
              )}

              {order.dispatch_remarks && (
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, fontSize: 13 }}>
                  <span className="muted">Dispatch Remarks:</span>
                  <span style={{ color: "#475569" }}>{order.dispatch_remarks}</span>
                </div>
              )}

              {/* Group Delivery: All Included Orders and Their Order Descriptions */}
              {order.batch_orders && order.batch_orders.length > 1 && (
                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: "1px solid var(--line)",
                  }}
                  data-testid="delivery-group-orders-section"
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Layers size={13} color="#2563eb" />
                    <span>Included Batch Products ({order.batch_orders.length})</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {order.batch_orders.map((bo) => {
                      const isCurrent = bo.order_id === order.order_id;
                      return (
                        <div
                          key={bo.order_id}
                          data-testid={`delivery-group-order-${bo.order_id}`}
                          onClick={() => handleSelectBatchOrder(bo)}
                          style={{
                            padding: "10px 12px",
                            background: isCurrent ? "#eff6ff" : "#f8fafc",
                            border: isCurrent ? "1.5px solid #3b82f6" : "1px solid var(--line)",
                            borderRadius: 6,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 4,
                            }}
                          >
                            <span
                              className="mono"
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: isCurrent ? "#1d4ed8" : "#2563eb",
                              }}
                            >
                              #{bo.order_id}
                            </span>
                            <span
                              className={`badge ${
                                bo.delivery_status === "Delivered"
                                  ? "green"
                                  : bo.delivery_status === "Out for Delivery"
                                  ? "blue"
                                  : "amber"
                              }`}
                              style={{ fontSize: 10, padding: "1px 6px" }}
                            >
                              {bo.delivery_status || "Pending"}
                            </span>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "baseline",
                              gap: 8,
                            }}
                          >
                            <b style={{ fontSize: 13, color: "#0f172a" }}>{bo.product}</b>
                            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                              {(bo.quantity || 0).toLocaleString()} {bo.unit || "units"}
                            </span>
                          </div>

                          <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.4 }}>
                            <span style={{ color: "#64748b", fontWeight: 600 }}>Order Description: </span>
                            <span
                              style={{
                                color: bo.description?.trim() ? "#1e293b" : "#94a3b8",
                                fontStyle: bo.description?.trim() ? "normal" : "italic",
                                wordBreak: "break-word",
                              }}
                              data-testid={`delivery-group-description-${bo.order_id}`}
                            >
                              {bo.description?.trim() ? bo.description.trim() : "No description provided"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Panel 3: Stage History & Audit Trail */}
          <section className="panel" data-testid="delivery-history-panel">
            <div className="panel-title">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Clock size={16} color="#0284c7" />
                <div>
                  <h3 style={{ margin: 0 }}>Delivery Stage History</h3>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    Audit trail of workflow transitions and stage corrections
                  </p>
                </div>
              </div>
              {order.current_stage !== "Order Received" && (
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ color: "#b45309", borderColor: "#fde68a", background: "#fffbeb", fontSize: 11.5 }}
                  onClick={() => setMovingBack(true)}
                  data-testid="delivery-history-move-back-btn"
                >
                  <RotateCcw size={11} /> Move Back
                </button>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <OrderHistoryTrail history={order.history || []} />
            </div>
          </section>
        </div>


        {/* RIGHT COLUMN: Assigned Agent & Delivery Action */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Panel 1: Assigned Delivery Person */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>Assigned Delivery Person</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Staff member responsible for transit
                </p>
              </div>
              <span className={`badge ${isDeliveryPersonAssigned ? "green" : "amber"}`}>
                {isDeliveryPersonAssigned ? "Assigned" : "Unassigned"}
              </span>
            </div>

            {/* Display Assigned Agent or Change Form */}
            {isDeliveryPersonAssigned && !isChangingAgent && (
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    padding: "12px 14px",
                    background: "#f8fafc",
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span className="muted" style={{ fontSize: 11, display: "block" }}>
                      Delivery Agent
                    </span>
                    <b style={{ fontSize: 15, color: "#0f172a", display: "block", marginTop: 2 }}>
                      {order.assigned_delivery_person}
                    </b>
                    {order.delivery_person_phone && (
                      <span style={{ fontSize: 12.5, color: "#475569", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                        <Phone size={12} /> {order.delivery_person_phone}
                      </span>
                    )}
                  </div>

                  {canAssignDelivery && !isDelivered && (
                    <button
                      type="button"
                      className="outline-btn"
                      style={{ padding: "6px 12px", fontSize: 12 }}
                      onClick={() => setIsChangingAgent(true)}
                    >
                      Change
                    </button>
                  )}
                </div>
              </div>
            )}

            {(!isDeliveryPersonAssigned || isChangingAgent) && canAssignDelivery && (
              <form onSubmit={handleAssignDeliveryPerson} style={{ marginTop: 14 }}>
                <div className="dispatch-field">
                  <label className="dispatch-label">
                    Select Delivery Person <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <select
                    className="dispatch-control"
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    required
                  >
                    {deliveryAgents.length === 0 ? (
                      <option value="" disabled>No Delivery Person available</option>
                    ) : (
                      <option value="">-- Choose Delivery Person --</option>
                    )}
                    {deliveryAgents.map((agent) => (
                      <option key={agent.user_id} value={agent.user_id}>
                        {agent.name} {agent.phone ? `(${agent.phone})` : ""}
                      </option>
                    ))}
                    <option value="custom">+ Enter New Delivery Person</option>
                  </select>
                </div>

                {selectedAgentId === "custom" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                    <div>
                      <label className="dispatch-label">Name *</label>
                      <input
                        className="dispatch-control"
                        type="text"
                        placeholder="e.g. Suresh Kumar"
                        value={customAgentName}
                        onChange={(e) => setCustomAgentName(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="dispatch-label">Phone</label>
                      <input
                        className="dispatch-control"
                        type="text"
                        placeholder="e.g. +91 98201 55667"
                        value={customAgentPhone}
                        onChange={(e) => setCustomAgentPhone(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={busyAction === "assign" || !selectedAgentId}
                    style={{ flex: 1 }}
                  >
                    {busyAction === "assign"
                      ? "Saving…"
                      : isChangingAgent
                      ? "Update Delivery Person"
                      : "Confirm Delivery Person"}
                  </button>
                  {isChangingAgent && (
                    <button
                      type="button"
                      className="outline-btn"
                      onClick={() => setIsChangingAgent(false)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )}

            {(!isDeliveryPersonAssigned || isChangingAgent) && !canAssignDelivery && (
              <div style={{ padding: "12px 14px", color: "var(--muted)", fontSize: 13 }}>
                No delivery driver assigned yet.
              </div>
            )}
          </section>

          {/* Panel 2: Delivery Stage Action */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>Delivery Workflow Action</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Transition order through delivery lifecycle
                </p>
              </div>
              <span className={`badge ${statusBadge}`}>
                {currentStage}
              </span>
            </div>

            <div style={{ marginTop: 14 }}>
              {isDelivered ? (
                /* Stage 3: Delivered */
                <div
                  style={{
                    padding: "18px 16px",
                    background: "#f8fafc",
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                    textAlign: "center",
                  }}
                >
                  <CheckCircle2 size={32} color="#16a34a" style={{ margin: "0 auto 8px" }} />
                  <b style={{ fontSize: 15, color: "#0f172a", display: "block" }}>
                    Delivery Completed
                  </b>
                  <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "#64748b" }}>
                    Received by: <b>{order.delivered_to || order.received_by || "Client"}</b> on{" "}
                    {formatDateTime(order.delivered_at || order.delivery_date)}
                  </p>
                  {order.delivery_remarks && (
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#475569", fontStyle: "italic" }}>
                      "{order.delivery_remarks}"
                    </p>
                  )}
                </div>
              ) : isOutForDelivery ? (
                /* Stage 2: Out for Delivery */
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div
                    style={{
                      padding: "12px 14px",
                      background: "#f8fafc",
                      border: "1px solid var(--line)",
                      borderRadius: 6,
                    }}
                  >
                    <b style={{ fontSize: 13.5, color: "#0f172a", display: "block" }}>
                      Currently Out for Delivery
                    </b>
                    <span style={{ fontSize: 12.5, color: "#475569", display: "block", marginTop: 4 }}>
                      Handed over to <b>{order.assigned_delivery_person || "Assigned Agent"}</b>
                      {order.out_for_delivery_at && ` on ${formatDateTime(order.out_for_delivery_at)}`}
                    </span>
                  </div>

                  {/* Challan Requirement Notice & Upload Action */}
                  {!hasChallan ? (
                    <div
                      style={{
                        padding: "12px 14px",
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: 6,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                      data-testid="detail-challan-required-box"
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: "#92400e", fontWeight: 600 }}>
                          Please upload a challan before marking this delivery as delivered.
                        </span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input
                          type="file"
                          id="detail-challan-upload"
                          accept=".pdf,image/*,.doc,.docx"
                          style={{ display: "none" }}
                          disabled={busyAction === "upload-challan"}
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleUploadChallan(e.target.files[0]);
                            }
                            e.target.value = "";
                          }}
                          data-testid="detail-challan-input"
                        />
                        <label
                          htmlFor="detail-challan-upload"
                          className="primary-btn"
                          style={{
                            background: "#d97706",
                            borderColor: "#d97706",
                            color: "#ffffff",
                            cursor: busyAction === "upload-challan" ? "wait" : "pointer",
                            fontSize: 13,
                            padding: "8px 16px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                          data-testid="detail-upload-challan-btn"
                        >
                          <Upload size={14} />
                          {busyAction === "upload-challan" ? "Uploading Challan…" : "Upload Challan First"}
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "10px 14px",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                      }}
                      data-testid="detail-challan-attached-box"
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#166534", fontSize: 13 }}>
                        <FileText size={14} />
                        <span>
                          Challan Attached: <b>{order.challan_number || "Document on file"}</b>
                        </span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        {order.challan_document_id && (
                          <a
                            href={downloadDocument(order.challan_document_id)}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              fontSize: 12,
                              color: "#15803d",
                              fontWeight: 600,
                              textDecoration: "underline",
                            }}
                          >
                            View Challan
                          </a>
                        )}
                        <input
                          type="file"
                          id="detail-challan-reupload"
                          accept=".pdf,image/*,.doc,.docx"
                          style={{ display: "none" }}
                          disabled={busyAction === "upload-challan"}
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleUploadChallan(e.target.files[0]);
                            }
                            e.target.value = "";
                          }}
                        />
                        <label
                          htmlFor="detail-challan-reupload"
                          style={{
                            fontSize: 12,
                            color: "#4b5563",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          title="Re-upload Challan"
                        >
                          Re-upload
                        </label>
                      </div>
                    </div>
                  )}

                  {!showCompleteForm ? (
                    <button
                      type="button"
                      className="primary-btn wide"
                      disabled={!hasChallan}
                      onClick={() => {
                        if (!hasChallan) {
                          toast.error("Please upload a challan before marking this delivery as delivered.");
                          return;
                        }
                        setShowCompleteForm(true);
                      }}
                      style={{
                        height: 44,
                        fontSize: 14,
                        fontWeight: 700,
                        background: !hasChallan ? "#94a3b8" : "#16a34a",
                        borderColor: !hasChallan ? "#94a3b8" : "#16a34a",
                        color: "#fff",
                        cursor: !hasChallan ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                      title={
                        !hasChallan
                          ? "Please upload a challan before marking this delivery as delivered."
                          : "Mark delivery complete"
                      }
                      data-testid="mark-complete-btn"
                    >
                      <CheckCircle2 size={16} /> Mark Delivery Complete
                    </button>
                  ) : (
                    <form
                      onSubmit={handleMarkDeliveryComplete}
                      style={{
                        padding: 14,
                        background: "#f8fafc",
                        border: "1px solid var(--line)",
                        borderRadius: 6,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <b style={{ fontSize: 13, color: "#0f172a", marginBottom: 12, display: "block" }}>
                        Confirm Delivery Completion
                      </b>


                      <div className="dispatch-field">
                        <label className="dispatch-label">Received By (Recipient Name) *</label>
                        <input
                          className="dispatch-control"
                          type="text"
                          placeholder="e.g. Ramesh Patel"
                          value={receivedBy}
                          onChange={(e) => setReceivedBy(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>

                      <div className="dispatch-field">
                        <label className="dispatch-label">Delivery Remarks (Optional)</label>
                        <input
                          className="dispatch-control"
                          type="text"
                          placeholder="e.g. Delivered in good condition"
                          value={deliveryRemarks}
                          onChange={(e) => setDeliveryRemarks(e.target.value)}
                        />
                      </div>

                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <button
                          type="submit"
                          className="primary-btn"
                          disabled={busyAction === "deliver" || !hasChallan}
                          style={{
                            flex: 1,
                            height: 40,
                            background: !hasChallan ? "#94a3b8" : "#16a34a",
                            borderColor: !hasChallan ? "#94a3b8" : "#16a34a",
                            color: "#ffffff",
                            cursor: !hasChallan ? "not-allowed" : "pointer",
                          }}
                          title={
                            !hasChallan
                              ? "Please upload a challan before marking this delivery as delivered."
                              : "Confirm Delivered"
                          }
                        >
                          {busyAction === "deliver" ? "Saving…" : "Confirm Delivered"}
                        </button>
                        <button
                          type="button"
                          className="outline-btn"
                          onClick={() => setShowCompleteForm(false)}
                          style={{ height: 40 }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              ) : (
                /* Stage 1: Ready for Delivery */
                <div>
                  <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px" }}>
                    This order is packaged and assigned. When the agent leaves with the shipment, send it out for delivery.
                  </p>

                  <div className="dispatch-field">
                    <label className="dispatch-label">Vehicle / Tracking Number (Optional)</label>
                    <input
                      className="dispatch-control"
                      type="text"
                      placeholder="e.g. MH-04-AB-1234"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="primary-btn wide"
                    disabled={!isDeliveryPersonAssigned || busyAction === "out-for-delivery"}
                    onClick={handleOutForDelivery}
                    style={{
                      height: 44,
                      fontSize: 14,
                      fontWeight: 700,
                      background: isDeliveryPersonAssigned ? "var(--navy)" : "#cbd5e1",
                      color: isDeliveryPersonAssigned ? "#ffffff" : "#64748b",
                      border: "none",
                      cursor: isDeliveryPersonAssigned ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <Truck size={16} />
                    {busyAction === "out-for-delivery" ? "Sending Out…" : "Send Out for Delivery"}
                  </button>

                  {!isDeliveryPersonAssigned && (
                    <p style={{ fontSize: 11.5, color: "#94a3b8", textAlign: "center", margin: "8px 0 0" }}>
                      * Please assign a delivery person above before sending out.
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Move Back Stage Modal */}
      {movingBack && order && (
        <MoveBackStageModal
          order={order}
          isOpen={movingBack}
          onClose={() => setMovingBack(false)}
          onSuccess={() => {
            loadOrder();
            onChange && onChange();
          }}
          user={user}
        />
      )}
    </div>
  );
}

